// src/pages/AdminDashboard.jsx
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../layouts/AdminLayout";
import "./AdminDashboard.css";

const API_BASE = "https://api.mariamlyluxe.com/public/api";
const MONTHS_WINDOW = 12; // change if you want 6/18 etc.
const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // headline cards (unchanged)
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalProducts: 0,
    totalOrders: 0,
    totalRevenue: 0,
    totalReviews: 0,
    pendingReviews: 0,
  });

  // monthly stats (orders & profit)
  const [ordersByMonth, setOrdersByMonth] = useState({
    months: [],
    orders: [],
    range: null,
  });

  const [revProfitByMonth, setRevProfitByMonth] = useState({
    months: [],
    revenue: [],
    profit: [],
    profitSplit: {},
    range: null,
    currency: "USD",
  });

  // helper for authorized fetch
  const apiFetch = async (path, token, options = {}) => {
    const res = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
        ...(options.headers || {}),
      },
    });
    return res;
  };

  useEffect(() => {
    const token =
      localStorage.getItem("access_token") || localStorage.getItem("token");
    const userRole = localStorage.getItem("user_role");
    const userData = localStorage.getItem("user");

    // Admin guard
    if (!token || userRole !== "1") {
      navigate("/");
      return;
    }

    if (userData) {
      try {
        setUser(JSON.parse(userData));
      } catch {}
    }

    (async () => {
      await fetchDashboardStats(token);
      await fetchMonthlyStats(token);
      setLoading(false);
    })();
  }, [navigate]);

  const fetchDashboardStats = async (token) => {
    try {
      const [usersRes, productsRes, reviewsRes] = await Promise.all([
        apiFetch("/users/count", token),
        apiFetch("/products/count", token),
        apiFetch("/admin/reviews", token),
      ]);

      if (
        usersRes.status === 401 || usersRes.status === 403 ||
        productsRes.status === 401 || productsRes.status === 403 ||
        reviewsRes.status === 401 || reviewsRes.status === 403
      ) {
        navigate("/");
        return;
      }

      if (!usersRes.ok) throw new Error(`Failed /users/count: ${usersRes.status}`);
      if (!productsRes.ok) throw new Error(`Failed /products/count: ${productsRes.status}`);

      const usersData = await usersRes.json();     // { total }
      const productsData = await productsRes.json(); // { total }
      
      let totalReviews = 0;
      let pendingReviews = 0;
      
      if (reviewsRes.ok) {
        const reviewsData = await reviewsRes.json();
        if (reviewsData.success && Array.isArray(reviewsData.data)) {
          totalReviews = reviewsData.data.length;
          pendingReviews = reviewsData.data.filter(review => !review.status).length;
        }
      }

      setStats((prev) => ({
        ...prev,
        totalUsers: Number(usersData.total) || 0,
        totalProducts: Number(productsData.total) || 0,
        totalOrders: prev.totalOrders || 89,   // keep placeholders you had
        totalRevenue: prev.totalRevenue || 12500,
        totalReviews: totalReviews,
        pendingReviews: pendingReviews,
      }));
    } catch (err) {
      console.error("Error fetching dashboard stats:", err);
      setStats((prev) => ({
        ...prev,
        totalUsers: prev.totalUsers || 0,
        totalProducts: prev.totalProducts || 0,
        totalReviews: prev.totalReviews || 0,
        pendingReviews: prev.pendingReviews || 0,
      }));
    }
  };

  // --- Normalizers ---
  const normalizeCountByMonth = (data, currentMonth) => {
    // shape A: { months: [...], series: { orders: [...] } }
    if (Array.isArray(data?.months) && data?.series?.orders) {
      return {
        months: data.months,
        orders: data.series.orders.map((n) => Number(n) || 0),
      };
    }
    // shape B: { success, year, counts: [{month:1..12,total:number}, ...] }
    if (Array.isArray(data?.counts)) {
      // Find the current month's data
      const currentMonthData = data.counts.find(row => Number(row.month) === currentMonth);
      const currentMonthTotal = currentMonthData ? Number(currentMonthData.total) || 0 : 0;
      
      return { 
        months: [MONTH_NAMES[currentMonth - 1]], 
        orders: [currentMonthTotal] 
      };
    }
    return { months: [], orders: [] };
  };

  const normalizeProfitByMonth = (data, currentMonth) => {
    // shape A: { months:[], series:{ revenue:[], profit:[] }, currency }
    if (Array.isArray(data?.months) && data?.series) {
      return {
        months: data.months,
        revenue: (data.series.revenue || []).map((n) => Number(n) || 0),
        profit: (data.series.profit || []).map((n) => Number(n) || 0),
        profitSplit: {},
        currency: data.currency || "USD",
      };
    }
    // shape B: single month response: { success, year, month, total_profit, split, raw }
    if (typeof data?.total_profit !== "undefined" && data?.month) {
      const monthName = MONTH_NAMES[Number(data.month) - 1];
      const profit = Number(data.total_profit) || 0;
      
      // Extract profit split data
      const profitSplit = data?.split ? {
        joumana: Number(data.split.joumana) || 0,
        ibrahim: Number(data.split.ibrahim) || 0
      } : {};
      
      return { 
        months: [monthName], 
        revenue: [0], // No separate revenue data in this format
        profit: [profit],
        profitSplit: profitSplit,
        currency: "USD"
      };
    }
    // shape C: yearly data with counts array (similar to orders)
    if (Array.isArray(data?.counts)) {
      const currentMonthData = data.counts.find(row => Number(row.month) === currentMonth);
      const currentMonthProfit = currentMonthData ? Number(currentMonthData.total_profit) || 0 : 0;
      
      return { 
        months: [MONTH_NAMES[currentMonth - 1]], 
        revenue: [0], // Assuming no separate revenue data in this format
        profit: [currentMonthProfit],
        profitSplit: {},
        currency: "USD"
      };
    }
    return { months: [], revenue: [], profit: [], profitSplit: {}, currency: "USD" };
  };

  // fetch monthly orders + profit
  const fetchMonthlyStats = async (token) => {
    try {
      const currentDate = new Date();
      const currentMonth = currentDate.getMonth() + 1; // 1-12
      const currentYear = currentDate.getFullYear();
      
      // Orders API returns yearly data, profit API returns single month data
      const ordersQs = `year=${currentYear}`;
      const profitQs = `year=${currentYear}&month=${currentMonth}`;
      const [countRes, profitRes] = await Promise.all([
        apiFetch(`/orders/stats/count-by-month?${ordersQs}`, token),
        apiFetch(`/orders/stats/profit-by-month?${profitQs}`, token),
      ]);

      if (countRes.status === 401 || countRes.status === 403) { navigate("/"); return; }
      if (profitRes.status === 401 || profitRes.status === 403) { navigate("/"); return; }

      if (countRes.ok) {
        const data = await countRes.json();
        const normalized = normalizeCountByMonth(data, currentMonth);
        setOrdersByMonth({ 
          months: normalized.months, 
          orders: normalized.orders, 
          range: data.range || null 
        });
      } else {
        const currentMonthName = MONTH_NAMES[currentMonth - 1];
        setOrdersByMonth({ months: [currentMonthName], orders: [0], range: null });
      }

      if (profitRes.ok) {
        const data = await profitRes.json();
        const normalized = normalizeProfitByMonth(data, currentMonth);
        setRevProfitByMonth({
          months: normalized.months,
          revenue: normalized.revenue,
          profit: normalized.profit,
          profitSplit: normalized.profitSplit || {},
          range: data.range || null,
          currency: normalized.currency || "USD",
        });
      } else {
        const currentMonthName = MONTH_NAMES[currentMonth - 1];
        setRevProfitByMonth({ 
          months: [currentMonthName], 
          revenue: [0], 
          profit: [0], 
          profitSplit: {},
          range: null, 
          currency: "USD" 
        });
      }
    } catch (e) {
      console.error("Error fetching monthly stats:", e);
      const currentDate = new Date();
      const currentMonth = currentDate.getMonth() + 1;
      const currentMonthName = MONTH_NAMES[currentMonth - 1];
      setOrdersByMonth({ months: [currentMonthName], orders: [0], range: null });
      setRevProfitByMonth({ months: [currentMonthName], revenue: [0], profit: [0], profitSplit: {}, range: null, currency: "USD" });
    }
  };

  // build the per-month rows (no grand totals)
  const rows = useMemo(
    () => mergeMonthlyRows(ordersByMonth, revProfitByMonth),
    [ordersByMonth, revProfitByMonth]
  );

  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-dashboard">
          <div className="loading">Loading...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-dashboard">
        {/* Header */}
        <div className="admin-header">
          <div className="admin-title">
            <h1>Dashboard Overview</h1>
            <p>
              Welcome back, {user?.fname} {user?.lname}
            </p>
          </div>
          <div className="admin-actions">
            <button className="btn-secondary" onClick={() => navigate("/")}>
              View Store
            </button>
          </div>
        </div>

        {/* Stats Cards (unchanged) */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon users">
              <svg viewBox="0 0 24 24">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" fill="none" stroke="currentColor" strokeWidth="2" />
                <circle cx="9" cy="7" r="4" fill="none" stroke="currentColor" strokeWidth="2" />
                <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" fill="none" stroke="currentColor" strokeWidth="2" />
              </svg>
            </div>
            <div className="stat-content">
              <h3>{stats.totalUsers.toLocaleString()}</h3>
              <p>Total Users</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon products">
              <svg viewBox="0 0 24 24">
                <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" fill="none" stroke="currentColor" strokeWidth="2" />
                <line x1="3" y1="6" x2="21" y2="6" stroke="currentColor" strokeWidth="2" />
                <path d="M16 10a4 4 0 0 1-8 0" fill="none" stroke="currentColor" strokeWidth="2" />
              </svg>
            </div>
            <div className="stat-content">
              <h3>{stats.totalProducts.toLocaleString()}</h3>
              <p>Total Products</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon reviews">
              <svg viewBox="0 0 24 24">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="currentColor"/>
              </svg>
            </div>
            <div className="stat-content">
              <h3>{stats.totalReviews.toLocaleString()}</h3>
              <p>Total Reviews</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon pending">
              <svg viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2"/>
                <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="stat-content">
              <h3>{stats.pendingReviews.toLocaleString()}</h3>
              <p>Pending Reviews</p>
            </div>
          </div>

          
        </div>
      </div>
    </AdminLayout>
  );
}

/* ---------------- helpers ---------------- */

function mergeMonthlyRows(ordersBlock, rpBlock) {
  const months =
    ordersBlock?.months?.length ? ordersBlock.months
    : rpBlock?.months?.length ? rpBlock.months
    : [];

  const orders = ordersBlock?.orders || [];
  const profit = rpBlock?.profit || [];

  return months.map((m, i) => ({
    month: m,
    orders: orders[i] ?? 0,
    profit: profit[i] ?? 0,
  }));
}
